import { formatMoney } from '@theme/money-formatting';

/**
 * Purchase options for bogo products: full price, or paid variant plus a gift variant.
 */
class OffhooBogoOptions extends HTMLElement {
  /** @type {Array<{id: number|string, title?: string, price: number, available: boolean, image?: string, max: number|null}>} */
  #variants = [];

  /** @type {Array<{id: number|string, price: number, available: boolean, max: number|null}>} */
  #paidVariants = [];

  connectedCallback() {
    this.#variants = this.#readVariants('[data-bogo-variants]');
    this.#paidVariants = this.#readVariants('[data-paid-variants]');
    if (!this.#paidVariants.length) this.#paidVariants = this.#variants;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onQtyChange);
    document.addEventListener('click', this.#onDocumentClick);
    this.syncPrices();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onQtyChange);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  /**
   * Refresh both card prices from the paid variant currently selected on the product form.
   */
  syncPrices() {
    const paid = this.#paidById(this.#paidVariantId());
    if (!paid) return;

    this.querySelectorAll('[data-bogo-card]').forEach((card) => {
      const priceEl = card.querySelector('[data-bogo-price]');
      if (!(priceEl instanceof HTMLElement)) return;
      priceEl.textContent = this.#format(paid.price * this.#cardQuantity(card));
    });
  }

  /**
   * Lines to send to checkout for the selected option.
   * Same paid and gift variant are merged so Buy X Get Y can discount one unit.
   * @returns {{ lines: Array<{variantId: string, quantity: number}>, error?: string }}
   */
  getCheckoutLines() {
    const mode = this.querySelector('input[type="radio"]:checked');
    const card = mode instanceof HTMLInputElement ? mode.closest('[data-bogo-card]') : null;
    const paidId = this.#paidVariantId();
    const quantity = this.#cardQuantity(card);
    const paid = this.#paidById(paidId);
    if (!paidId || quantity < 1 || (paid && paid.available === false)) {
      return { lines: [], error: this.dataset.giftUnavailable || '' };
    }

    if (!(mode instanceof HTMLInputElement) || mode.value !== 'bogo') {
      if (paid && !this.#fitsInventory(paid, quantity)) {
        return { lines: [], error: this.dataset.giftUnavailable || '' };
      }
      return { lines: [{ variantId: paidId, quantity }] };
    }

    const giftId = this.#giftVariantId();
    const gift = this.#variantById(giftId);
    if (!giftId || !gift || gift.available === false) {
      return { lines: [], error: this.dataset.giftUnavailable || '' };
    }

    if (giftId === paidId) {
      const paired = quantity * 2;
      if (paid && !this.#fitsInventory(paid, paired)) {
        return { lines: [], error: this.dataset.giftUnavailable || '' };
      }
      return { lines: [{ variantId: paidId, quantity: paired }] };
    }

    if ((paid && !this.#fitsInventory(paid, quantity)) || !this.#fitsInventory(gift, quantity)) {
      return { lines: [], error: this.dataset.giftUnavailable || '' };
    }

    return {
      lines: [
        { variantId: paidId, quantity },
        { variantId: giftId, quantity },
      ],
    };
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const stepButton = target.closest('[data-bogo-step]');
    if (stepButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      const card = stepButton.closest('[data-bogo-card]');
      const input = card?.querySelector('[data-bogo-qty]');
      if (!(input instanceof HTMLInputElement) || !card) return;
      const dir = Number(stepButton.dataset.bogoStep) || 0;
      const next = Math.max(1, (Number(input.value) || 1) + dir);
      input.value = String(next);
      this.#selectCard(card);
      this.syncPrices();
      return;
    }

    const option = target.closest('[data-bogo-gift-id]');
    if (option instanceof HTMLButtonElement && !option.disabled) {
      event.preventDefault();
      event.stopPropagation();
      this.#selectGift(option);
      return;
    }

    const toggle = target.closest('.offhoo-bogo__gift-toggle');
    if (toggle instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#toggleMenu(toggle);
      return;
    }

    const card = target.closest('[data-bogo-card]');
    if (card instanceof HTMLElement) this.#selectCard(card);
  };

  /** @param {Event} event */
  #onQtyChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-bogo-qty')) return;
    const next = Math.max(1, Number(input.value) || 1);
    input.value = String(next);
    this.syncPrices();
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!(event.target instanceof Node) || this.contains(event.target)) return;
    this.#closeMenu();
  };

  /**
   * @param {HTMLElement} card
   */
  #selectCard(card) {
    const radio = card.querySelector('input[type="radio"]');
    if (radio instanceof HTMLInputElement) radio.checked = true;
  }

  /**
   * @param {HTMLButtonElement} option
   */
  #selectGift(option) {
    this.querySelectorAll('[data-bogo-gift-id]').forEach((button) => {
      button.classList.toggle('is-selected', button === option);
      button.setAttribute('aria-selected', button === option ? 'true' : 'false');
    });
    const image = this.querySelector('.offhoo-bogo__gift-image');
    const source = option.querySelector('img');
    if (image instanceof HTMLImageElement && source instanceof HTMLImageElement && source.src) {
      image.src = source.src;
    }
    const card = option.closest('[data-bogo-card]');
    if (card instanceof HTMLElement) this.#selectCard(card);
    this.#closeMenu();
  }

  /**
   * @param {HTMLButtonElement} toggle
   */
  #toggleMenu(toggle) {
    const menu = toggle.parentElement?.querySelector('.offhoo-bogo__menu');
    if (!(menu instanceof HTMLElement)) return;
    const open = menu.hasAttribute('hidden');
    this.#closeMenu();
    if (!open) return;
    menu.removeAttribute('hidden');
    toggle.setAttribute('aria-expanded', 'true');
  }

  #closeMenu() {
    this.querySelectorAll('.offhoo-bogo__menu').forEach((menu) => menu.setAttribute('hidden', ''));
    this.querySelectorAll('.offhoo-bogo__gift-toggle').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  /** @returns {string} */
  #paidVariantId() {
    const input = this.closest('form')?.querySelector('input[name="id"]');
    return input instanceof HTMLInputElement ? input.value : '';
  }

  /** @returns {string} */
  #giftVariantId() {
    const selected = this.querySelector('[data-bogo-gift-id][aria-selected="true"], [data-bogo-gift-id].is-selected');
    if (selected instanceof HTMLElement && selected.dataset.bogoGiftId) return selected.dataset.bogoGiftId;
    const hidden = this.querySelector('input[data-bogo-gift-id]');
    return hidden instanceof HTMLInputElement ? hidden.value : '';
  }

  /**
   * @param {string} selector
   */
  #readVariants(selector) {
    const json = this.querySelector(selector)?.textContent || '[]';
    try {
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string} id
   */
  #paidById(id) {
    return this.#paidVariants.find((variant) => String(variant.id) === String(id));
  }

  /**
   * @param {string} id
   */
  #variantById(id) {
    return this.#variants.find((variant) => String(variant.id) === String(id));
  }

  /**
   * @param {Element | null} card
   * @returns {number}
   */
  #cardQuantity(card) {
    const input = card?.querySelector('[data-bogo-qty]');
    const qty = input instanceof HTMLInputElement ? Number(input.value) : 1;
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  /**
   * @param {{ max: number|null, available: boolean }} variant
   * @param {number} quantity
   */
  #fitsInventory(variant, quantity) {
    if (variant.available === false) return false;
    if (variant.max == null) return true;
    return quantity <= variant.max;
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #format(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'EUR');
  }
}

if (!customElements.get('offhoo-bogo-options')) {
  customElements.define('offhoo-bogo-options', OffhooBogoOptions);
}
